import { ApiClient } from "../../api/ApiClient";
import { getUuid } from "../../utilities/Number";
import { supportedLinkingServiceTypesSelector } from "../reducers/AuthReducer";
import { apiErrorCodes } from "../../api/ApiResponse";
import { getTickersForIds } from "./TickerActions";
import {
  SET_BACKGROUND_LINK_FAILURE_DATA,
  SET_POPULAR_PROVIDERS,
  isZaboToInHouseApiCandidate,
  isZaboToInHouseOauthCandidate,
  tickerSubTypes,
  getAccountLinkingService
} from "./Common";
import {
  UNKNOWN_TICKER_SHORT_NAME,
  getTickerUsingId,
  categoryType,
  isCryptoLinkingService,
  accountLinkingService
} from "../reducers/Common";

export const aiAppraiserAssetTypes = {
  REAL_ESTATE: "real estate",
  VEHICLE: "vehicle",
  DOMAIN: "domain"
};

export const qrBasedProviderIds = [
  "jpmel_oauth_client_gb",
  "revolut_client_oauth_gb",
  "fakebank_two_step_interactive_xf"
];

export const setBackgroundLinkFailureDataAction = failureData => ({
  type: SET_BACKGROUND_LINK_FAILURE_DATA,
  failureData
});

export const setPopularProvidersAction = (category, providerEntries) => ({
  type: SET_POPULAR_PROVIDERS,
  category,
  providerEntries
});

export const getAggregatorName = linkType => {
  switch (linkType) {
    case 1:
      return "Yodlee";
    case 2:
      return "Plaid";
    case 3:
      return "Zabo";
    case 4:
    case 5:
      return "Salt Edge";
    case 6:
      return "Zillow";
    case 7:
      return "Estibot";
    case 8:
      return "Vinaudit";
    case 9:
      return "Zerion";
    case 10:
    case 11:
      return "Crypto";
    case 12:
    case 13:
      return "Flinks";
    case 14:
      return "Akahu";
    case 15:
      return "Lean";
    case 16:
      return "Mastercard";
    case 17:
      return "SnapTrade";
    case 18:
      return "MX";
    case 19:
      return "Kubera Portfolio";
    case 20:
      return "Direct API";
    case 21:
      return "Manual Account";
    case 23:
      return "Akoya";
    case 24:
      return "AI Appraiser";
    default:
      return null;
  }
};

export const accountLinkingContainers = {
  NFT: "nft",
  INVESTMENT: "investment"
};

const hardwareWalletsProviders = ["trezor", "ledger", "phantomwallet", "keplrwallet"];

export const isHardwareWalletProvider = (linkType, linkProviderId) => {
  return (
    (linkType === accountLinkingService.IN_HOUSE_CRYPTO_API || linkType === accountLinkingService.ZABO) &&
    hardwareWalletsProviders.includes(linkProviderId)
  );
};

export const isZaboReconnectionCandidate = (linkType, linkProviderId) => {
  if (!linkProviderId === true || linkType !== accountLinkingService.ZABO) {
    return false;
  }
  return ["coinbase-wallet", "atomicwallet", "trust", "exodus"].includes(linkProviderId.toLowerCase());
};

export const isZaboManualEntryCandidate = (linkType, linkProviderId) => {
  if (!linkProviderId === true || linkType !== accountLinkingService.ZABO) {
    return false;
  }
  return (
    isZaboToInHouseApiCandidate(linkProviderId) === false &&
    isZaboToInHouseOauthCandidate(linkProviderId) === false &&
    isZaboReconnectionCandidate(linkProviderId) === false
  );
};

export const zaboMigrateProviderId = linkProviderId => {
  if (!linkProviderId === true) {
    return linkProviderId;
  }
  if (linkProviderId.toLowerCase() === "xpub-key") {
    return "xpubbitcoin";
  }
  return linkProviderId.toLowerCase();
};

export const isLinkingServiceSupported = (state, linkingService) => {
  const supportedLinkingServiceTypes = supportedLinkingServiceTypesSelector(state);

  if (linkingService && supportedLinkingServiceTypes) {
    return supportedLinkingServiceTypes.includes(linkingService);
  }
  return false;
};

export const isPlaidSupportedForCountry = countryCode => {
  if (!countryCode === true) {
    return false;
  }

  const plaidSupportedCountries = ["US", "CA"];
  return plaidSupportedCountries.includes(countryCode.toUpperCase());
};

export const getAccountLinkingServiceType = (state, countryCode = null) => {
  if (isLinkingServiceSupported(state, accountLinkingService.PLAID) && isPlaidSupportedForCountry(countryCode)) {
    return accountLinkingService.PLAID;
  }
  return accountLinkingService.YODLEE;
};

export const getServiceNameForCountry = (state, countryCode) => {
  return getAggregatorName(getAccountLinkingServiceType(state, countryCode));
};

export const prefetchPopularProviders = categories => {
  return dispatch => {
    for (const category of categories) {
      dispatch(
        fetchPopularProviders(
          null,
          category,
          providers => {
            var linkingServiceToProviderMap = {};

            for (const [index, provider] of providers.entries()) {
              var linkingService = null;

              provider.index = index;

              if (provider.aggregatorOptions) {
                const primaryAggregator = provider.aggregatorOptions.find(option => option.plan === "a");
                if (primaryAggregator) {
                  linkingService = primaryAggregator.linkType;
                }
              }

              if (linkingService) {
                provider.linkType = linkingService;

                if (!linkingServiceToProviderMap[linkingService] === false) {
                  const serviceProviders = linkingServiceToProviderMap[linkingService];
                  serviceProviders.push(provider);
                } else {
                  linkingServiceToProviderMap[linkingService] = [provider];
                }
              }
            }

            var providerEntries = [];
            for (const popularProviderService in linkingServiceToProviderMap) {
              const providersEntry = {
                providers: linkingServiceToProviderMap[popularProviderService],
                category: category,
                linkingService: parseInt(popularProviderService)
              };

              providerEntries.push(providersEntry);
            }
            dispatch(setPopularProvidersAction(category, providerEntries));
          },
          null
        )
      );
    }
  };
};

export const fetchPopularProviders = (linkType, category, onSuccess, onError) => {
  return dispatch => {
    var providerType = "any";
    if (isCryptoLinkingService(linkType)) {
      providerType = "crypto";
    } else if (!linkType === false) {
      providerType = "fiat";
    }

    ApiClient.getPopularProviders(getUuid(), providerType, category)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        if (onError) {
          onError(apiError);
        }
      });
  };
};

export const searchProviders = (
  linkType,
  category,
  searchText,
  aggregatorToBeSkipped,
  linkProviderIdToBeSkipped,
  onSuccess,
  onError
) => {
  return dispatch => {
    var providerType = "fiat";
    if (isCryptoLinkingService(linkType)) {
      providerType = "crypto";
    }

    ApiClient.searchProviders(
      getUuid(),
      providerType,
      "Any",
      searchText,
      aggregatorToBeSkipped,
      linkProviderIdToBeSkipped
    )
      .then(apiData => {
        onSuccess(searchText, apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getJwtToken = (linkType, category, action, providerId, providerName, onSuccess, onError) => {
  return dispatch => {
    ApiClient.getJwtToken(getUuid(), linkType, category, action, providerId, providerName)
      .then(apiData => {
        onSuccess("Bearer " + apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getPublicTokenForCustodian = (linkType, custodianId, onSuccess, onError) => {
  return dispatch => {
    ApiClient.getPublicTokenForCustodian(getUuid(), linkType, custodianId)
      .then(apiData => {
        onSuccess(apiData.payload.publicToken);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getLinkTokenForCustodian = (linkType, linkDetails, onSuccess, onError) => {
  return dispatch => {
    ApiClient.getLinkTokenForCustodian(getUuid(), linkType, linkDetails)
      .then(apiData => {
        onSuccess(apiData.payload.linkToken);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const setPublicTokenForCustodian = (linkType, custodianId, params, onSuccess, onError) => {
  return dispatch => {
    if (process.env.REACT_APP_ENV === "local") {
      params.test = 1;
    }
    ApiClient.setPublicTokenForCustodian(getUuid(), linkType, custodianId, params)
      .then(apiData => {
        onSuccess();
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getLinkedAccounts = (
  linkType,
  category,
  params,
  onSuccess = () => null,
  onError = () => null,
  shouldRetry = () => true
) => {
  return (dispatch, getState) => {
    const maxRetry = 30;
    var retryCount = 0;

    const fetchAccounts = (error = null) => {
      retryCount++;
      if (shouldRetry() === false) {
        onError(error);
        return;
      }
      if (retryCount > maxRetry) {
        onError(error);
        return;
      }

      ApiClient.getLinkedAccountList(getUuid(), linkType, category, params)
        .then(apiData => {
          const accounts = apiData.payload;

          var unknownTickerIds = new Set();
          for (const account of accounts) {
            if (getTickerUsingId(getState(), account.tickerId).shortName === UNKNOWN_TICKER_SHORT_NAME) {
              unknownTickerIds.add(account.tickerId);
            }
          }

          if (unknownTickerIds.size === 0) {
            onSuccess(accounts);
          } else {
            dispatch(
              getTickersForIds(
                Array.from(unknownTickerIds),
                newTickers => {
                  onSuccess(accounts, newTickers);
                },
                () => {
                  onSuccess(accounts);
                }
              )
            );
          }
        })
        .catch(apiError => {
          if (
            apiError.errorCode === apiErrorCodes.PRODUCT_NOT_READY ||
            apiError.httpStatus === 504 ||
            apiError.httpStatus === 502 ||
            apiError.httpStatus === 499
          ) {
            setTimeout(() => {
              fetchAccounts(apiError);
            }, 10000);
          } else {
            onError(apiError);
          }
        });
    };
    fetchAccounts();
  };
};

export const getProviderDetails = (linkType, providerId, onSuccess, onError) => {
  return dispatch => {
    ApiClient.getProviderDetails(getUuid(), linkType, providerId)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const deleteProviderAccount = (linkType, providerAccountId) => {
  return dispatch => {
    ApiClient.deleteProviderAccount(getUuid(), linkType, providerAccountId)
      .then(apiData => {})
      .catch(apiError => {});
  };
};

export const getLinkedConnectionsList = (groupId, onSuccess, onError) => {
  return dispatch => {
    ApiClient.getLinkedConnectionsList(getUuid(), groupId)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getLinkConnectionUrl = (linkType, linkProviderId, onSuccess, onError) => {
  return dispatch => {
    var params = {};
    if (process.env.REACT_APP_ENV === "local") {
      params.test = 1;
    }

    ApiClient.getLinkConnectionUrl(getUuid(), linkType, linkProviderId, params)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getRefreshConnectionUrl = (linkType, linkProviderAccountId, onSuccess, onError) => {
  return dispatch => {
    var params = {};
    if (process.env.REACT_APP_ENV === "local") {
      params.test = 1;
    }

    ApiClient.getRefreshConnectionUrl(getUuid(), linkType, linkProviderAccountId, params)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getReconnectConnectionUrl = (linkType, linkProviderAccountId, onSuccess, onError) => {
  return dispatch => {
    var params = {};
    if (process.env.REACT_APP_ENV === "local") {
      params.test = 1;
    }

    ApiClient.getReconnectConnectionUrl(getUuid(), linkType, linkProviderAccountId, params)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getHandoffToken = (onSuccess, onError) => {
  return dispatch => {
    ApiClient.getHandoffToken(getUuid())
      .then(apiData => {
        const payload = apiData.payload;
        const handoffToken =
          payload && typeof payload === "object" ? payload.token || payload.handoffToken || payload.id : payload;
        onSuccess(handoffToken);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getHandoffTokenInfo = (handoffToken, onSuccess, onError) => {
  return dispatch => {
    ApiClient.getHandoffTokenInfo(getUuid(), handoffToken)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const setHandoffTokenInfo = (handoffToken, info, onSuccess, onError) => {
  return dispatch => {
    ApiClient.setHandoffTokenInfo(getUuid(), handoffToken, info)
      .then(apiData => {
        onSuccess(apiData ? apiData.payload : null);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const searchTickerInfo = (term, type, subType) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.searchTickerInfo(getUuid(), term, type, subType)
        .then(apiData => {
          resolve(apiData);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const searchHomesInfo = (term, custodianId, type = "all") => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.searchHomesInfo(getUuid(), term.trim(), custodianId, type)
        .then(apiData => {
          resolve(apiData);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const linkAndGetAddressDetails = (custodianId, selected) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      const body = {
        portfolioId: custodianId,
        providerId: "zillow",
        providerName: "zillow",
        metaData: JSON.stringify(selected)
      };

      ApiClient.linkAndGetAddressDetails(getUuid(), body)
        .then(apiData => {
          resolve(apiData ? apiData.payload : null);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const getVehicleDetails = vinNumber => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.getVehicleDetails(getUuid(), vinNumber)
        .then(apiData => {
          resolve(apiData ? apiData.payload : null);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const linkAndGetVehicleDetails = (custodianId, selected) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      const body = {
        portfolioId: custodianId,
        providerId: "vinaudit",
        providerName: "vinaudit",
        metaData: JSON.stringify(selected)
      };

      ApiClient.linkAndGetVehicleDetails(getUuid(), body)
        .then(apiData => {
          resolve(apiData ? apiData.payload : null);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const appraiseAssetValue = (desc, portfolioCurrency) => {
  return dispatch => {
    const limitedDesc = desc.slice(0, 1000);
    return new Promise((resolve, reject) => {
      ApiClient.appraiseAssetValue(getUuid(), limitedDesc, portfolioCurrency)
        .then(apiData => {
          const documentId = apiData?.payload?.documentId;
          if (!documentId) {
            reject(new Error("No documentId returned from appraiseAssetValue"));
            return;
          }

          const maxRetries = 7;
          let retryCount = 0;

          const pollAppraisal = () => {
            ApiClient.getAssetAppraisal(getUuid(), documentId)
              .then(appraisalData => {
                const results = appraisalData?.payload;
                if (results?.[0]?.status === "in-progress") {
                  retryCount++;
                  if (retryCount >= maxRetries) {
                    reject(new Error("Asset appraisal timed out after maximum retries"));
                    return;
                  }
                  setTimeout(pollAppraisal, 10_000);
                } else if (results?.[0]?.error) {
                  reject(new Error(results?.[0]?.error));
                  return;
                } else {
                  resolve(results);
                }
              })
              .catch(apiError => {
                reject(apiError);
              });
          };

          setTimeout(pollAppraisal, 10_000);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const linkAndGetAiAppraiserDetails = (portfolioId, selected) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const params = {
        portfolioId: portfolioId,
        providerId: getAccountLinkingService(accountLinkingService.AI_APPRAISER),
        providerName: getAccountLinkingService(accountLinkingService.AI_APPRAISER),
        metaData: JSON.stringify(selected)
      };

      ApiClient.getLinkedAccountList(getUuid(), accountLinkingService.AI_APPRAISER, categoryType.ASSET, params)
        .then(apiData => {
          const accounts = apiData.payload;
          var unknownTickerIds = new Set();
          for (const account of accounts) {
            if (getTickerUsingId(getState(), account.tickerId).shortName === UNKNOWN_TICKER_SHORT_NAME) {
              unknownTickerIds.add(account.tickerId);
            }
          }

          if (unknownTickerIds.size === 0) {
            resolve(accounts);
          } else {
            dispatch(
              getTickersForIds(
                Array.from(unknownTickerIds),
                () => {
                  resolve(accounts);
                },
                () => {
                  resolve(accounts);
                }
              )
            );
          }
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const utilityStatus = logProps => {
  return async dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.utilityStatus(getUuid(), logProps)
        .then(apiData => {
          resolve(apiData ? apiData.payload : null);
        })
        .catch(apiError => {
          reject(apiError);
        });
    }).catch(apiError => {
      console.log(apiError);
    });
  };
};

export const getConnectionInfo = (linkType, providerId, onSuccess, onError) => {
  return dispatch => {
    ApiClient.getConnectionInfo(getUuid(), linkType, providerId)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getReConnectionInfo = (linkType, providerAccountId, onSuccess, onError) => {
  return dispatch => {
    ApiClient.getReConnectionInfo(getUuid(), linkType, providerAccountId)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const setLinkingProviderStatus = (linkType, groupId, providerId, status) => {
  return dispatch => {
    ApiClient.setLinkingProviderStatus(getUuid(), linkType, groupId, providerId, status)
      .then(apiData => {})
      .catch(apiError => {});
  };
};

export const shouldShowAccountListContextMenuItem = linkType => {
  return (
    linkType !== accountLinkingService.ZILLOW &&
    linkType !== accountLinkingService.DOMAINS &&
    linkType !== accountLinkingService.CARS &&
    linkType !== accountLinkingService.AI_APPRAISER &&
    linkType !== accountLinkingService.ZERION &&
    linkType !== accountLinkingService.IN_HOUSE_CRYPTO_API &&
    linkType !== accountLinkingService.IN_HOUSE_CRYPTO_OAUTH &&
    linkType !== accountLinkingService.IN_HOUSE_OAUTH
  );
};

export const mapAiAppraiserContainerToTickerSubType = assetType => {
  switch (assetType) {
    case aiAppraiserAssetTypes.REAL_ESTATE:
      return tickerSubTypes.HOME;
    case aiAppraiserAssetTypes.VEHICLE:
      return tickerSubTypes.CARS;
    case aiAppraiserAssetTypes.DOMAIN:
      return tickerSubTypes.DOMAINS;
  }
  return null;
};

export const mapTickerSubTypeToAiAppraiserContainer = subType => {
  switch (subType) {
    case tickerSubTypes.HOME:
      return aiAppraiserAssetTypes.REAL_ESTATE;
    case tickerSubTypes.CARS:
      return aiAppraiserAssetTypes.VEHICLE;
    case tickerSubTypes.DOMAINS:
      return aiAppraiserAssetTypes.DOMAIN;
  }
  return null;
};
